import { Dag, DagStatus } from '@agilelab/plugin-wb-builder-common';
import {
  createActionsColumn,
  TableCellProps,
  WbBadge,
} from '@agilelab/plugin-wb-platform';
import { Test } from '../../ControlPanel';
import { snakeCaseToTitleCase } from '@agilelab/plugin-wb-platform-common';
import { Box, Typography } from '@material-ui/core';
import { format, parseISO } from 'date-fns';
import { ReactNode } from 'react';
import { LatestDeployProcess } from '../ProvisioningCard/LatestProvisioningAccordion/LatestDeployProcess';
import { DeploySteps } from '../ProvisioningCard/LatestProvisioningAccordion/DeploySteps';
import { LogFileButton } from '../LogFileButton';
import { Deploy } from '../types';

export const getActionLabel = (action: string) => {
  switch (action) {
    case 'PROVISION':
      return 'Deploy';
    case 'UNPROVISION':
      return 'Undeploy';
    default:
      return action;
  }
};

export const createReleaseColumn = (): TableCellProps<Deploy> => ({
  headerName: 'Release',
  field: 'version',
  sortable: false,
  fieldRender: row => <Typography>{row.version ?? '-'}</Typography>,
});

export const createActionColumn = (
  headerName = 'Action',
): TableCellProps<Deploy> => ({
  headerName,
  field: 'action',
  fieldRender: row => {
    const label = getActionLabel(row.action);

    const color = row.action === 'UNPROVISION' ? 'secondary' : 'primary';
    return <WbBadge color={color}>{label}</WbBadge>;
  },
});

export const createDateColumn = (
  sortable: boolean = false,
): TableCellProps<Deploy> => ({
  headerName: 'Date',
  field: 'deployDate',
  sortable,
  fieldRender: row => (
    <Typography>
      {format(parseISO(row.deployDate), 'yyyy/MM/dd HH:mm:ss')}
    </Typography>
  ),
});

export const createStatusColumn = (
  stepIcons: Record<string, ReactNode>,
): TableCellProps<Deploy> => ({
  headerName: 'Status',
  field: 'status',
  fieldRender: row => (
    <Box display="flex" alignItems="center" style={{ gap: '4px' }}>
      {stepIcons[row.status as DagStatus]}
      <Typography style={{ fontWeight: 700 }}>
        {snakeCaseToTitleCase(row.status.toLowerCase())}
      </Typography>
    </Box>
  ),
});

export const createProcessColumn = (): TableCellProps<Deploy> => ({
  headerName: 'Process',
  field: 'steps' as keyof Deploy,
  width: '40%',
  fieldRender: row => <LatestDeployProcess deploy={row} />,
});

export const createHistoryProcessColumn = (
  testsMap: Map<string, Test>,
  onStepClick: (step: Dag, deploy: Deploy) => void,
  onValidationGatewayClick: (
    deploy: Deploy,
    subPlanId: string | undefined,
    test: Test | undefined,
  ) => void,
): TableCellProps<Deploy> => ({
  headerName: 'Process',
  field: 'steps' as keyof Deploy,
  width: '40%',
  fieldRender: row => (
    <DeploySteps
      deploy={row}
      testsMap={testsMap}
      onStepClick={(step, d) => onStepClick(step, d)}
      onValidationGatewayClick={onValidationGatewayClick}
    />
  ),
});

export const createLogColumn = (): TableCellProps<Deploy> =>
  createActionsColumn({
    width: 100,
    renderActions: row => <LogFileButton deploy={row} />,
  });
